"use client";

/**
 * "A page change is on its way": set by a link the moment Next starts a
 * client-side navigation (`onNavigate`), cleared when the new route
 * renders (NavigationWatcher). The header's mark turns while it is set
 * (owner, 2026-10-19: "whenever we change a page ... it spins").
 *
 * Shown only after SHOW_AFTER_MS, so a page that is already prefetched
 * swaps in without a twitch; dropped after MAX_MS whatever happens, so a
 * navigation that never commits cannot leave the mark spinning.
 */
import { useSyncExternalStore } from "react";

const SHOW_AFTER_MS = 150;
const MAX_MS = 15_000;

let pending = false;
let showTimer: ReturnType<typeof setTimeout> | undefined;
let maxTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

function samePage(href: string): boolean {
  try {
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin
      && url.pathname === window.location.pathname
      && url.search === window.location.search;
  } catch {
    return false;
  }
}

/** A link is taking the visitor somewhere else. */
export function startNavigation(href?: string): void {
  if (typeof window === "undefined") return;
  if (href && samePage(href)) return; // a hash jump or the page itself: nothing will render
  clearTimeout(showTimer);
  clearTimeout(maxTimer);
  showTimer = setTimeout(() => {
    pending = true;
    emit();
  }, SHOW_AFTER_MS);
  maxTimer = setTimeout(endNavigation, MAX_MS);
}

/** The new page is on screen. */
export function endNavigation(): void {
  clearTimeout(showTimer);
  clearTimeout(maxTimer);
  if (pending) {
    pending = false;
    emit();
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function useNavigationPending(): boolean {
  return useSyncExternalStore(subscribe, () => pending, () => false);
}

/** Wrap a Link's ``onNavigate``: run the caller's handler, then start the
 *  wait unless the caller cancelled the navigation. */
export function withNavigationSpin(
  href: unknown,
  onNavigate?: (e: { preventDefault: () => void }) => void,
) {
  return (e: { preventDefault: () => void }) => {
    let cancelled = false;
    onNavigate?.({
      preventDefault: () => {
        cancelled = true;
        e.preventDefault();
      },
    });
    if (!cancelled) startNavigation(typeof href === "string" ? href : undefined);
  };
}
